import { useState, useEffect, useCallback, useMemo } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Plus, Pencil, Trash2, Printer, ClipboardList, RefreshCw, Search, MessageSquare, ImageIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PRIORITY_ORDER = ['URGENT', 'ASAP', 'NEXT', 'SCHEDULED'];
const PRIORITY_COLOURS = {
  URGENT:    'bg-red-100 text-red-800 border-red-400',
  ASAP:      'bg-orange-100 text-orange-800 border-orange-400',
  NEXT:      'bg-amber-100 text-amber-800 border-amber-400',
  SCHEDULED: 'bg-blue-100 text-blue-800 border-blue-400',
};
const STATUS_COLOURS = {
  NEW:         'bg-slate-100 text-slate-800 border-slate-300',
  IN_PROGRESS: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  ON_HOLD:     'bg-yellow-100 text-yellow-800 border-yellow-300',
  COMPLETE:    'bg-emerald-100 text-emerald-800 border-emerald-300',
  CANCELLED:   'bg-red-100 text-red-800 border-red-300',
};

const blank = () => ({
  subject: '', description: '', assigned_to: '', priority: 'SCHEDULED',
  time_frame_days: 1, due_date: '', status: 'NEW', notes: '',
});

export default function JobCards() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('open');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [commentDlg, setCommentDlg] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [photoLightbox, setPhotoLightbox] = useState(null); // { jobId, photos, index }

  // Cache photo blob URLs so <img> tags don't need auth headers.
  const [photoBlobs, setPhotoBlobs] = useState({});
  const loadPhotoBlob = useCallback(async (photo) => {
    if (photoBlobs[photo.id]) return photoBlobs[photo.id];
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}${photo.url}`, { responseType: 'blob' });
      const objUrl = URL.createObjectURL(r.data);
      setPhotoBlobs((prev) => ({ ...prev, [photo.id]: objUrl }));
      return objUrl;
    } catch { return null; }
  }, [photoBlobs]);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { include_completed: filterStatus === 'all' };
      if (filterStatus !== 'all' && filterStatus !== 'open') params.status = filterStatus;
      if (filterPriority !== 'all') params.priority = filterPriority;
      const r = await axios.get(`${API}/job-cards`, { params });
      setJobs(r.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load job cards'); }
    finally { setLoading(false); }
  }, [filterPriority, filterStatus]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const filtered = useMemo(() => {
    if (!search) return jobs;
    const q = search.toLowerCase();
    return jobs.filter(j =>
      (j.subject || '').toLowerCase().includes(q) ||
      (j.description || '').toLowerCase().includes(q) ||
      (j.assigned_to || '').toLowerCase().includes(q),
    );
  }, [jobs, search]);

  const openNew = () => { setEditing(null); setForm(blank()); setShowForm(true); };
  const openEdit = (j) => {
    setEditing(j);
    setForm({
      subject: j.subject, description: j.description, assigned_to: j.assigned_to || '',
      priority: j.priority, time_frame_days: j.time_frame_days || 0,
      due_date: j.due_date || '', status: j.status, notes: j.notes || '',
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.subject.trim() || !form.description.trim()) {
      toast.error('Subject and description are required'); return;
    }
    try {
      if (editing) {
        await axios.put(`${API}/job-cards/${editing.id}`, form);
        toast.success('Job card updated');
      } else {
        await axios.post(`${API}/job-cards`, form);
        toast.success('Job card created');
      }
      setShowForm(false);
      await fetchJobs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (j) => {
    if (!window.confirm(`Delete job card "${j.subject}"?`)) return;
    try {
      await axios.delete(`${API}/job-cards/${j.id}`);
      toast.success('Deleted');
      await fetchJobs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const addComment = async () => {
    if (!commentText.trim()) return;
    try {
      await axios.post(`${API}/job-cards/${commentDlg.id}/comments`, { text: commentText.trim() });
      setCommentText('');
      const r = await axios.get(`${API}/job-cards/${commentDlg.id}`);
      setCommentDlg(r.data);
      await fetchJobs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Comment failed'); }
  };

  return (
    <DashboardLayout>
      <div className="p-4 space-y-4">
        {/* Header + controls (hidden when printing) */}
        <div className="flex items-center justify-between gap-2 flex-wrap print:hidden">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <ClipboardList className="h-6 w-6" /> Job Cards
            </h1>
            <p className="text-slate-300 text-sm">Capture, assign and track jobs across the workshop.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="h-3 w-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search subject / assignee…" className="pl-7 w-56 bg-slate-800 border-slate-700 text-white" data-testid="jobs-search" />
            </div>
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="w-36 bg-slate-800 border-slate-700 text-white" data-testid="jobs-filter-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                {PRIORITY_ORDER.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-36 bg-slate-800 border-slate-700 text-white" data-testid="jobs-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open only</SelectItem>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="NEW">New</SelectItem>
                <SelectItem value="IN_PROGRESS">In progress</SelectItem>
                <SelectItem value="ON_HOLD">On hold</SelectItem>
                <SelectItem value="COMPLETE">Complete</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchJobs} disabled={loading} data-testid="jobs-refresh">
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button variant="outline" onClick={() => window.print()} data-testid="jobs-print">
              <Printer className="h-4 w-4 mr-1" /> Print
            </Button>
            <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700" data-testid="jobs-new">
              <Plus className="h-4 w-4 mr-1" /> New Job Card
            </Button>
          </div>
        </div>

        {/* Print-only header */}
        <div className="hidden print:block">
          <h1 className="text-3xl font-black">JSM — Job Cards</h1>
          <p className="text-sm">Printed {new Date().toLocaleString('en-ZA')} · {filtered.length} card(s)</p>
          <hr className="my-2 border-black" />
        </div>

        <Card className="bg-slate-900/70 border-slate-700 print:bg-white print:border-black">
          <CardHeader className="pb-2 print:hidden">
            <CardTitle className="text-white text-base">Active Job Cards ({filtered.length})</CardTitle>
            <CardDescription className="text-slate-400">Priority: URGENT → ASAP → NEXT → SCHEDULED</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto border border-slate-700 rounded print:border-black">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-800/80 print:bg-slate-200">
                    <TableHead className="text-slate-300 print:text-black">Subject / Vehicle</TableHead>
                    <TableHead className="text-slate-300 print:text-black">Description</TableHead>
                    <TableHead className="text-slate-300 print:text-black">Assigned To</TableHead>
                    <TableHead className="text-slate-300 print:text-black">Priority</TableHead>
                    <TableHead className="text-slate-300 print:text-black">Timeframe</TableHead>
                    <TableHead className="text-slate-300 print:text-black">Due</TableHead>
                    <TableHead className="text-slate-300 print:text-black">Status</TableHead>
                    <TableHead className="text-slate-300 print:text-black text-right print:hidden">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-6">Loading…</TableCell></TableRow>
                  ) : filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-6">No job cards. Click <b>New Job Card</b> to add one.</TableCell></TableRow>
                  ) : filtered.map(j => (
                    <TableRow key={j.id} className="hover:bg-slate-800/60 print:bg-white" data-testid={`job-row-${j.id}`}>
                      <TableCell className="text-white print:text-black font-bold whitespace-nowrap">{j.subject}</TableCell>
                      <TableCell className="text-slate-200 print:text-black text-sm max-w-md">
                        <div>{j.description}</div>
                        {j.photos?.length > 0 && (
                          <JobPhotoStrip
                            job={j}
                            loadBlob={loadPhotoBlob}
                            photoBlobs={photoBlobs}
                            onOpen={(idx) => setPhotoLightbox({ jobId: j.id, photos: j.photos, index: idx })}
                          />
                        )}
                      </TableCell>
                      <TableCell className="text-slate-200 print:text-black text-sm">{j.assigned_to || '—'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={PRIORITY_COLOURS[j.priority]}>{j.priority}</Badge>
                      </TableCell>
                      <TableCell className="text-slate-200 print:text-black text-sm">{j.time_frame_days ? `${j.time_frame_days} day${j.time_frame_days > 1 ? 's' : ''}` : '—'}</TableCell>
                      <TableCell className="text-slate-200 print:text-black text-sm whitespace-nowrap">{j.due_date || '—'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={STATUS_COLOURS[j.status]}>{j.status.replace('_', ' ')}</Badge>
                      </TableCell>
                      <TableCell className="text-right print:hidden">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost" onClick={() => { setCommentDlg(j); setCommentText(''); }} data-testid={`job-comment-${j.id}`}>
                            <MessageSquare className="h-3 w-3" />
                            {j.comments?.length ? <span className="ml-1 text-xs">{j.comments.length}</span> : null}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openEdit(j)} data-testid={`job-edit-${j.id}`}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          {isAdmin && (
                            <Button size="sm" variant="ghost" onClick={() => remove(j)} data-testid={`job-delete-${j.id}`}>
                              <Trash2 className="h-3 w-3 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Create / Edit Dialog */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Job Card' : 'New Job Card'}</DialogTitle>
              <DialogDescription>Capture what needs to be done, who&apos;s doing it, and when.</DialogDescription>
            </DialogHeader>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label>Subject / Vehicle *</Label>
                <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="e.g. Ben's bukkie · NP200 CFM94394" data-testid="job-form-subject" />
              </div>
              <div className="md:col-span-2">
                <Label>Description *</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Indicator globes to be replaced" rows={2} data-testid="job-form-description" />
              </div>
              <div>
                <Label>Assigned To</Label>
                <Input value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} placeholder="e.g. Ikkie Workshop" data-testid="job-form-assigned" />
              </div>
              <div>
                <Label>Priority *</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="job-form-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITY_ORDER.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Time-frame (days)</Label>
                <Input type="number" min="0" value={form.time_frame_days} onChange={(e) => setForm({ ...form, time_frame_days: parseInt(e.target.value, 10) || 0 })} data-testid="job-form-days" />
              </div>
              <div>
                <Label>Due Date (auto if blank)</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="job-form-due" />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="job-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NEW">NEW</SelectItem>
                    <SelectItem value="IN_PROGRESS">IN PROGRESS</SelectItem>
                    <SelectItem value="ON_HOLD">ON HOLD</SelectItem>
                    <SelectItem value="COMPLETE">COMPLETE</SelectItem>
                    <SelectItem value="CANCELLED">CANCELLED</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="job-form-notes" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="job-form-save">{editing ? 'Save Changes' : 'Create Job Card'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Comments dialog */}
        <Dialog open={!!commentDlg} onOpenChange={(o) => !o && setCommentDlg(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Comments — {commentDlg?.subject}</DialogTitle>
              <DialogDescription>{commentDlg?.description}</DialogDescription>
            </DialogHeader>
            <div className="max-h-64 overflow-y-auto space-y-2 border rounded p-2 bg-slate-50">
              {(commentDlg?.comments || []).length === 0 ? (
                <p className="text-center text-slate-500 text-sm py-4">No comments yet.</p>
              ) : commentDlg.comments.map(c => (
                <div key={c.id} className="text-sm border-b last:border-0 pb-2">
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>{c.by} · {c.role}</span>
                    <span>{new Date(c.at).toLocaleString('en-ZA')}</span>
                  </div>
                  <div className="text-slate-800">{c.text}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Add a comment…" data-testid="job-comment-input"
                onKeyDown={(e) => { if (e.key === 'Enter') addComment(); }} />
              <Button onClick={addComment} data-testid="job-comment-add">Post</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Photo lightbox */}
        <Dialog open={!!photoLightbox} onOpenChange={(o) => !o && setPhotoLightbox(null)}>
          <DialogContent className="max-w-3xl" data-testid="job-photo-lightbox">
            <DialogHeader>
              <DialogTitle>Vehicle photos ({(photoLightbox?.index ?? 0) + 1}/{photoLightbox?.photos?.length || 0})</DialogTitle>
              <DialogDescription>
                Attached by staff during inspection · <span className="italic">click a thumbnail to switch</span>
              </DialogDescription>
            </DialogHeader>
            {photoLightbox && (() => {
              const current = photoLightbox.photos[photoLightbox.index];
              const src = photoBlobs[current?.id];
              return (
                <div className="space-y-3">
                  <div className="bg-slate-900 rounded flex items-center justify-center min-h-[400px]">
                    {src ? (
                      <img src={src} alt={current.filename} className="max-h-[500px] max-w-full object-contain" />
                    ) : (
                      <div className="text-white text-sm p-8">Loading photo…</div>
                    )}
                  </div>
                  {photoLightbox.photos.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {photoLightbox.photos.map((p, i) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setPhotoLightbox({ ...photoLightbox, index: i })}
                          className={`shrink-0 rounded border-2 w-16 h-16 overflow-hidden ${i === photoLightbox.index ? 'border-cyan-500' : 'border-transparent hover:border-slate-400'}`}
                          data-testid={`lightbox-thumb-${i}`}
                        >
                          {photoBlobs[p.id]
                            ? <img src={photoBlobs[p.id]} alt="" className="w-full h-full object-cover" />
                            : <div className="w-full h-full bg-slate-700" />
                          }
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="text-xs text-slate-500">
                    Uploaded {current?.uploaded_at ? new Date(current.uploaded_at).toLocaleString('en-ZA') : '—'}
                    {current?.source === 'vehicle_report' && ' · from a standalone vehicle report'}
                    {current?.source === 'shift_inspection' && ' · from an on-duty shift inspection'}
                  </div>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

// ---------------------------------------------------------------------------
// Photo strip rendered under each job card's description.
// Loads each photo lazily as blob (so the workshop can see it inline).
// ---------------------------------------------------------------------------
function JobPhotoStrip({ job, loadBlob, photoBlobs, onOpen }) {
  const photos = job.photos || [];
  useEffect(() => {
    // Prefetch first up-to-4 thumbnails.
    photos.slice(0, 4).forEach((p) => { if (!photoBlobs[p.id]) loadBlob(p); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id]);
  const shown = photos.slice(0, 4);
  const extra = photos.length - shown.length;
  return (
    <div className="mt-2 flex gap-1 items-center flex-wrap" data-testid={`job-photos-${job.id}`}>
      <ImageIcon className="h-3 w-3 text-slate-400" />
      <span className="text-[10px] text-slate-400 mr-1">{photos.length} photo{photos.length === 1 ? '' : 's'}</span>
      {shown.map((p, i) => {
        const src = photoBlobs[p.id];
        return (
          <button
            key={p.id}
            type="button"
            className="w-10 h-10 rounded border border-slate-600 overflow-hidden hover:border-cyan-400 transition-colors"
            onClick={() => onOpen(i)}
            data-testid={`job-photo-thumb-${p.id}`}
            title={p.filename}
          >
            {src
              ? <img src={src} alt="" className="w-full h-full object-cover" />
              : <div className="w-full h-full bg-slate-700 animate-pulse" />
            }
          </button>
        );
      })}
      {extra > 0 && (
        <button
          type="button"
          onClick={() => onOpen(4)}
          className="w-10 h-10 rounded border border-slate-600 bg-slate-800 text-xs text-slate-200 hover:border-cyan-400"
          data-testid={`job-photo-more-${job.id}`}
        >
          +{extra}
        </button>
      )}
    </div>
  );
}
